import { Loading } from '@bumble/app/components/Loading';
import HighlightOffOutlinedIcon from '@mui/icons-material/HighlightOffOutlined';
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
} from '@mui/material';
import { useEffect, useRef, useState } from 'react';
import { useApi } from '../hooks/use-api';
import { useMutation } from '@tanstack/react-query';
import { apiError } from '../utils/api-error';
import { toast } from 'react-toastify';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import html2canvas from 'html2canvas';
import { jsPDF } from 'jspdf';

interface AppVisibiltityModalTableProps {
  open: boolean;
  handleClose: () => void;
  data: {
    app_name: string;
    bytes: number;
    description: string;
    hits: number;
    port: string;
    protocol: string;
    duration?: number;
  };
  title: string;
}

const CAPTURE_ATTR = 'data-capture-id';
const CAPTURE_ID = 'app-analysis-content';

export const AppVisibilityModalTable = ({
  open,
  handleClose,
  data,
  title,
}: AppVisibiltityModalTableProps) => {
  const [analyzeStarted, setAnalyzeStarted] = useState(false);
  const [analyzeEnded, setAnalyzeEnded] = useState(false);
  const [analysisText, setAnalysisText] = useState<string | null>(null);
  const [downloading, setDownloading] = useState(false);
  const [mounted, setMounted] = useState(false);

  const api = useApi();
  const contentRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setMounted(true);
  }, []);

  const appAnalysis = useMutation(api.dashboardAppAnalysisApiV1DashboardAppAnalysisGet, {
    onSuccess: async (res) => {
      const reader = res.body?.getReader();
      const decoder = new TextDecoder();

      if (!reader) {
        toast.error('No readable stream found in response.');
        setAnalyzeStarted(false);
        return;
      }

      let result = '';
      let done = false;
      while (!done) {
        const { value, done: streamDone } = await reader.read();
        if (value) result += decoder.decode(value, { stream: true });
        done = streamDone;
      }

      setAnalysisText(result || 'No analysis data available.');
      setAnalyzeStarted(false);
      setAnalyzeEnded(true);
      toast.success('Analysis completed successfully.');
    },
    onError: apiError,
  });

  const startAnalyze = () => {
    setAnalyzeStarted(true);
    setAnalyzeEnded(false);
    appAnalysis.mutate({
      app_name: data.app_name,
      protocol: data.protocol,
      port: data.port,
    });
  };

  useEffect(() => {
    if (mounted && data) startAnalyze();
  }, [mounted, data]);

  const handleModalClose = () => {
    setAnalyzeStarted(false);
    setAnalyzeEnded(false);
    setAnalysisText(null);
    setDownloading(false);
    handleClose();
  };

  const handleDownload = async () => {
    if (!contentRef.current) return;

    setDownloading(true);
    try {
      const canvas = await html2canvas(contentRef.current, {
        scale: 2,
        useCORS: true,
        backgroundColor: '#ffffff',
        windowWidth: contentRef.current.scrollWidth,
        windowHeight: contentRef.current.scrollHeight,
        onclone: (doc) => {
          const cloneEl = doc.querySelector<HTMLElement>(
            `[${CAPTURE_ATTR}="${CAPTURE_ID}"]`
          );
          if (cloneEl) {
            cloneEl.style.maxHeight = 'none';
            cloneEl.style.overflowY = 'visible';
          }
        },
      });

      const imgData = canvas.toDataURL('image/png');
      const pdf = new jsPDF('p', 'pt', 'a4');
      const pdfWidth = pdf.internal.pageSize.getWidth();
      const pdfHeight = pdf.internal.pageSize.getHeight();
      const imgWidth = pdfWidth;
      const imgHeight = (canvas.height * imgWidth) / canvas.width;

      let heightLeft = imgHeight;
      let position = 0;

      pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight);
      heightLeft -= pdfHeight;

      while (heightLeft > 0) {
        position = -(imgHeight - heightLeft);
        pdf.addPage();
        pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight);
        heightLeft -= pdfHeight;
      }

      pdf.save('Bumblebee-app-analysis-report.pdf');
    } catch (err) {
      console.error(err);
      toast.error('Error generating PDF.');
    } finally {
      setDownloading(false);
    }
  };

  if (!data) return <Loading />;

  return (
    <Dialog
      open={open}
      fullWidth
      maxWidth="md"
      onClose={handleModalClose}
      aria-labelledby="analysis-dialog"
    >
      <DialogTitle
        id="analysis-dialog"
        sx={{ fontSize: '18px', display: 'flex', alignItems: 'center' }}
        color="primary"
      >
        {title}
      </DialogTitle>

      <DialogContent sx={{ padding: 3 }}>
        {analyzeStarted && <Loading />}

        {analyzeEnded && analysisText && (
          <Box
            ref={contentRef}
            {...{ [CAPTURE_ATTR]: CAPTURE_ID }}
            sx={{
              backgroundColor: '#fff',
              padding: 3,
              maxHeight: '70vh',
              overflowY: 'auto',
              border: '1px solid #e0e0e0',
            }}
          >
            <ReactMarkdown remarkPlugins={[remarkGfm]}>
              {analysisText}
            </ReactMarkdown>
          </Box>
        )}

        {analyzeEnded && (
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
            {downloading && <CircularProgress sx={{ mr: 2 }} />}
            <Button
              onClick={handleDownload}
              variant="outlined"
              sx={{ mr: 2 }}
              disabled={downloading}
            >
              Download
            </Button>
            <Button onClick={handleModalClose} variant="outlined">
              Close
            </Button>
          </Box>
        )}
      </DialogContent>

      <DialogActions>
        <IconButton
          sx={{
            position: 'absolute',
            top: '8px',
            right: '8px',
          }}
          onClick={handleModalClose}
        >
          <HighlightOffOutlinedIcon fontSize="small" />
        </IconButton>
      </DialogActions>
    </Dialog>
  );
};
